import { useCallback, useEffect, useState } from "react"
import { motion } from "framer-motion"
import {
    TrendingUp,
    Users,
    Music,
    ExternalLink,
    Play,
    Calendar,
    MapPin,
    Award,
    Instagram,
    Youtube,
    Twitter,
    ChevronRight,
    Star,
} from "lucide-react"
import { HomepageLoader } from "../components/Homepage/HomepageLoader";
import { SITE_URL, PAGE_OG_IMAGES } from "../seo/constants";
import { generateMetadata } from "../seo/metadataGenerator";
import { generateBreadcrumbSchema, generateGraphSchema, generateWebPageSchema } from "../seo/schemaGenerator";
import SeoHead from "../seo/SeoHead";
import { usePrerenderReady } from "../hooks/usePrerenderReady";

export default function CaseStudiesPage() {
    usePrerenderReady();
    const [selectedGenre, setSelectedGenre] = useState("all")
    const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
    const [isHovering, setIsHovering] = useState(false);
    // const [isLoading, setIsLoading] = useState(true);

    const updateCursorPosition = useCallback((e) => {
        requestAnimationFrame(() => {
            setCursorPosition({ x: e.clientX, y: e.clientY });
        });
    }, []);

    useEffect(() => {
        document.body.style.cursor = "none";
        const handleMouseEnter = () => setIsHovering(true);
        const handleMouseLeave = () => setIsHovering(false);

        const interactiveElements = document.querySelectorAll(
            'a, button, input, [role="button"]'
        );
        interactiveElements.forEach((element) => {
            element.addEventListener("mouseenter", handleMouseEnter);
            element.addEventListener("mouseleave", handleMouseLeave);
        });

        window.addEventListener("mousemove", updateCursorPosition);

        // Simulate loading time (remove this in production and use real loading logic)
        // const timer = setTimeout(() => {
        //     setIsLoading(false);
        // }, 3000);

        return () => {
            // clearTimeout(timer);
            document.body.style.cursor = "auto";
            window.removeEventListener("mousemove", updateCursorPosition);
            interactiveElements.forEach((element) => {
                element.removeEventListener("mouseenter", handleMouseEnter);
                element.removeEventListener("mouseleave", handleMouseLeave);
            });
        };
    }, [updateCursorPosition]);

    const genres = [
        { id: "all", label: "All Genres", count: 12 },
        { id: "pop", label: "Pop", count: 4 },
        { id: "hip-hop", label: "Hip-Hop", count: 3 },
        { id: "electronic", label: "Electronic", count: 2 },
        { id: "indie", label: "Indie", count: 2 },
        { id: "r&b", label: "R&B", count: 1 },
    ]

    const caseStudies = [
        {
            id: 1,
            featured: true,
            artistName: "Maya Chen",
            genre: "r&b",
            location: "Los Angeles, CA",
            image: "/karan-profile.png",
            title: "From Bedroom Producer to 50K Monthly Listeners",
            subtitle: "How Maya grew her R&B fanbase 100x in 6 months",
            description:
                "Independent R&B artist Maya Chen was struggling to get her music heard beyond her local scene. Using UpNComer's AI marketing tools, she transformed her career while keeping 100% of her royalties.",
            results: {
                streamGrowth: "10,000%",
                monthlyListeners: "50,000",
                playlistPlacements: "127",
                revenueIncrease: "2,400%",
            },
            timeline: "6 months",
            tools: ["AI Marketing Manager", "Smart Playlist Targeting", "Professional Mastering", "Analytics Suite"],
            quote:
                "UpNComer didn't just help me grow my streams - it gave me back control of my music career. I'm making more money as an independent artist than I ever thought possible.",
            socialLinks: {
                spotify: "https://open.spotify.com/user/3142hxn2i3wyimc3yj25skyerhpu?si=7a3cda9f9b574736",
                instagram: "https://www.instagram.com/upncomer.co/",
                youtube: "https://www.youtube.com/@upncomer",
            },
            link: "https://blog.upncomer.com/up-case-study",
        },
        {
            id: 2,
            featured: false,
            artistName: "DJ Luna",
            genre: "electronic",
            location: "Berlin, Germany",
            image: "/karan-profile.png",
            title: "Viral TikTok Success with AI Content Creation",
            subtitle: "2M views in 48 hours using automated content strategy",
            description:
                "Electronic producer DJ Luna used UpNComer's Content AI to create viral TikTok content, leading to massive streaming growth and international recognition.",
            results: {
                streamGrowth: "3,200%",
                monthlyListeners: "125,000",
                tiktokViews: "2,000,000",
                revenueIncrease: "1,800%",
            },
            timeline: "3 months",
            tools: ["Content Creator AI", "Social Media Automation", "Viral Trend Analysis", "Cross-Platform Distribution"],
            quote:
                "The AI content tools helped me create content that actually resonates with my audience. I went viral without compromising my artistic vision.",
            socialLinks: {
                spotify: "https://open.spotify.com/artist/dj-luna",
                tiktok: "https://tiktok.com/@djlunaofficial",
                soundcloud: "https://soundcloud.com/djluna",
            },
            link: "https://blog.upncomer.com/dj-luna-case-study",
        },
        {
            id: 3,
            featured: false,
            artistName: "The Midnight Collective",
            genre: "indie",
            location: "Nashville, TN",
            image: "/karan-profile.png",
            title: "Band's Breakthrough with Strategic Release Planning",
            subtitle: "From local gigs to international streaming success",
            description:
                "Indie rock band The Midnight Collective used UpNComer's strategic release planning and AI marketing to break out of the local scene and reach fans worldwide.",
            results: {
                streamGrowth: "1,500%",
                monthlyListeners: "75,000",
                playlistPlacements: "89",
                tourBookings: "45",
            },
            timeline: "8 months",
            tools: ["Release Planning", "AI Marketing Manager", "Fan Engagement Tools", "Tour Promotion"],
            quote:
                "UpNComer helped us think strategically about our releases. We're not just throwing music into the void anymore - we have a real plan.",
            socialLinks: {
                spotify: "https://open.spotify.com/artist/midnight-collective",
                instagram: "https://instagram.com/midnightcollectiveband",
                website: "https://midnightcollective.com",
            },
            link: "https://blog.upncomer.com/midnight-collective-case-study",
        },
        {
            id: 4,
            featured: false,
            artistName: "Rico Martinez",
            genre: "hip-hop",
            location: "Miami, FL",
            image: "/karan-profile.png",
            title: "Hip-Hop Artist's Journey to Playlist Domination",
            subtitle: "127 playlist placements in 4 months",
            description:
                "Rising hip-hop artist Rico Martinez leveraged UpNComer's playlist targeting AI to secure placements on major Spotify playlists, dramatically increasing his reach.",
            results: {
                streamGrowth: "2,800%",
                monthlyListeners: "95,000",
                playlistPlacements: "127",
                labelInterest: "3 offers",
            },
            timeline: "4 months",
            tools: ["Smart Playlist Targeting", "AI Marketing Manager", "Professional Mastering", "Industry Networking"],
            quote:
                "The playlist targeting feature is insane. I'm getting placed on playlists I never would have found on my own. It's like having a whole A&R team working for me.",
            socialLinks: {
                spotify: "https://open.spotify.com/artist/rico-martinez",
                instagram: "https://instagram.com/ricomartinezmusic",
                twitter: "https://twitter.com/ricomartinezrap",
            },
            link: "https://blog.upncomer.com/rico-martinez-case-study",
        },
        {
            id: 5,
            featured: false,
            artistName: "Stellar Dreams",
            genre: "pop",
            location: "London, UK",
            image: "/karan-profile.png",
            title: "Pop Duo's Rise to International Recognition",
            subtitle: "From bedroom recordings to radio play",
            description:
                "Pop duo Stellar Dreams used UpNComer's complete suite of tools to transform from bedroom producers to internationally recognized artists with radio play across Europe.",
            results: {
                streamGrowth: "4,200%",
                monthlyListeners: "180,000",
                radioStations: "67",
                revenueIncrease: "3,100%",
            },
            timeline: "10 months",
            tools: ["Complete AI Suite", "Radio Promotion", "International Marketing", "Professional Mastering"],
            quote:
                "UpNComer gave us everything we needed to compete with major label artists. We're proof that independent artists can make it big with the right tools.",
            socialLinks: {
                spotify: "https://open.spotify.com/artist/stellar-dreams",
                instagram: "https://instagram.com/stellardreamsmusic",
                youtube: "https://youtube.com/stellardreams",
            },
            link: "https://blog.upncomer.com/stellar-dreams-case-study",
        },
        {
            id: 6,
            featured: false,
            artistName: "Bass Therapy",
            genre: "electronic",
            location: "Amsterdam, Netherlands",
            image: "/karan-profile.png",
            title: "Electronic Producer's Festival Circuit Success",
            subtitle: "From SoundCloud to main stage performances",
            description:
                "Electronic music producer Bass Therapy used UpNComer's tools to build a massive following and secure bookings at major European festivals.",
            results: {
                streamGrowth: "5,600%",
                monthlyListeners: "220,000",
                festivalBookings: "12",
                fanGrowth: "890%",
            },
            timeline: "7 months",
            tools: ["Fan Engagement Tools", "Event Promotion", "Social Media Automation", "Analytics Suite"],
            quote:
                "The fan engagement tools helped me build a real community around my music. Now I have fans traveling across Europe to see my shows.",
            socialLinks: {
                spotify: "https://open.spotify.com/artist/bass-therapy",
                soundcloud: "https://soundcloud.com/basstherapy",
                instagram: "https://instagram.com/basstherapymusic",
            },
            link: "https://blog.upncomer.com/bass-therapy-case-study",
        },
    ]

    const filteredCaseStudies =
        selectedGenre === "all" ? caseStudies : caseStudies.filter((study) => study.genre === selectedGenre)

    const featuredStudy = caseStudies.find((study) => study.featured)
    const regularStudies = caseStudies.filter((study) => !study.featured)

    //SEO
    const metadata = generateMetadata({
        title:
            "Music Artist Success Stories & Case Studies | UpNComer",

        description:
            "Explore real-world case studies and artist success stories covering music marketing, distribution, fan engagement, release strategies and audience growth. UpNComer shares insights, performance breakdowns and proven workflows that help independent musicians grow careers, increase streams and build stronger music brands.",

        image: PAGE_OG_IMAGES.caseStudies,

        path: "/case-studies",
    })

    const webPageSchema = generateWebPageSchema({
        title:
            "Music Artist Success Stories & Case Studies | UpNComer",

        description:
            "Explore real-world case studies and artist success stories covering music marketing, distribution, fan engagement, release strategies and audience growth. UpNComer shares insights, performance breakdowns and proven workflows that help independent musicians grow careers, increase streams and build stronger music brands.",

        url: `${SITE_URL}/case-studies`,
    })

    const breadcrumbItems = [
        {
            "name": "Home",
            "path": "/"
        },
        {
            "name": "Case Studies",
            "path": "/case-studies"
        }
    ]

    const schemas = generateGraphSchema([
        webPageSchema,
        generateBreadcrumbSchema(breadcrumbItems)
    ])

    return (
        <>
            {/* {isLoading && <HomepageLoader />} */}
            <SeoHead
                metadata={metadata}
                schemas={schemas}
            />
            <div
                className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
                style={{
                    left: `${cursorPosition.x}px`,
                    top: `${cursorPosition.y}px`,
                    transform: `translate(-50%, -50%)`,
                }}
            >
                <div className="inner-circle"></div>
            </div>
            <div
                className="transition-all duration-1000 opacity-100 blur-none"
            >
                <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
                    {/* Enhanced Multi-layered Background */}
                    <div className="absolute inset-0 z-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

                        {/* Floating orbs */}
                        <motion.div
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.3, 0.6, 0.3],
                            }}
                            transition={{
                                duration: 10,
                                repeat: Number.POSITIVE_INFINITY,
                                ease: "easeInOut",
                            }}
                            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
                        />

                        <motion.div
                            animate={{
                                scale: [1, 1.3, 1],
                                opacity: [0.2, 0.5, 0.2],
                            }}
                            transition={{
                                duration: 12,
                                repeat: Number.POSITIVE_INFINITY,
                                ease: "easeInOut",
                                delay: 3,
                            }}
                            className="absolute bottom-32 left-16 w-72 h-72 bg-gradient-to-br from-[#FA6138]/12 via-[#818cf8]/15 to-[#6366f1]/18 rounded-full blur-3xl"
                        />
                    </div>
                    <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
                        <div className="container max-w-7xl mx-auto">
                            {/* Header */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                                className="text-center mb-16"
                            >
                                <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
                                    Success Stories
                                </h1>
                                <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-xl font-light max-w-3xl mx-auto leading-relaxed">
                                    Real artists, real results. See how independent musicians are transforming their careers with UpNComer
                                </p>
                            </motion.div>

                            {/* Stats Overview */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16"
                            >
                                {[
                                    { value: "450+", label: "Artists Growing", icon: <Users className="w-6 h-6" /> },
                                    { value: "2.3M+", label: "Streams Generated", icon: <Music className="w-6 h-6" /> },
                                    { value: "89%", label: "Success Rate", icon: <TrendingUp className="w-6 h-6" /> },
                                    { value: "$50K+", label: "Revenue Protected", icon: <Award className="w-6 h-6" /> },
                                ].map((stat, index) => (
                                    <div
                                        key={index}
                                        className="bg-[#1e3a8a]/15 backdrop-blur-xl border border-[#3b82f6]/20 rounded-2xl p-6 text-center hover:bg-[#3b82f6]/10 transition-all duration-300"
                                        style={{
                                            boxShadow: "0 10px 30px rgba(30, 58, 138, 0.2), inset 0 1px 0 rgba(59, 130, 246, 0.1)",
                                        }}
                                    >
                                        <div className="flex justify-center mb-3 text-[#FA6138]">{stat.icon}</div>
                                        <div className="text-2xl font-bold text-[#c7d2fe] mb-1">{stat.value}</div>
                                        <div className="text-[#818cf8]/70 text-sm">{stat.label}</div>
                                    </div>
                                ))}
                            </motion.div>

                            {/* Genre Filter */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="flex flex-wrap justify-center gap-3 mb-12"
                            >
                                {genres.map((genre) => (
                                    <button
                                        key={genre.id}
                                        onClick={() => setSelectedGenre(genre.id)}
                                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${selectedGenre === genre.id
                                            ? "bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white shadow-lg"
                                            : "bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40"
                                            } backdrop-blur-xl`}
                                    >
                                        {genre.label} ({genre.count})
                                    </button>
                                ))}
                            </motion.div>

                            {/* Featured Case Study */}
                            {featuredStudy && selectedGenre === "all" && (
                                <motion.div
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.6 }}
                                    className="mb-16"
                                >
                                    <div
                                        className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl overflow-hidden group hover:border-[#a5b4fc]/30 transition-all duration-500"
                                        style={{
                                            boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                                        }}
                                    >
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                            <div className="p-8 lg:p-12 flex flex-col justify-center">
                                                <div className="flex items-center gap-4 mb-6">
                                                    <span className="bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white px-3 py-1 rounded-full text-xs font-medium">
                                                        FEATURED SUCCESS
                                                    </span>
                                                    <div className="flex items-center text-[#818cf8]/60 text-sm">
                                                        <MapPin className="w-4 h-4 mr-1" />
                                                        {featuredStudy.location}
                                                    </div>
                                                </div>

                                                <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4 leading-tight">
                                                    {featuredStudy.title}
                                                </h2>
                                                <p className="text-xl text-[#FA6138] mb-6 font-medium">{featuredStudy.subtitle}</p>
                                                <p className="text-[#818cf8]/80 mb-8 leading-relaxed">{featuredStudy.description}</p>

                                                {/* Results Grid */}
                                                <div className="grid grid-cols-2 gap-4 mb-8">
                                                    <div className="bg-[#1e3a8a]/20 rounded-lg p-4 text-center">
                                                        <div className="text-2xl font-bold text-[#FA6138] mb-1">
                                                            {featuredStudy.results.streamGrowth}
                                                        </div>
                                                        <div className="text-[#93c5fd] text-sm">Stream Growth</div>
                                                    </div>
                                                    <div className="bg-[#1e3a8a]/20 rounded-lg p-4 text-center">
                                                        <div className="text-2xl font-bold text-[#FA6138] mb-1">
                                                            {featuredStudy.results.monthlyListeners}
                                                        </div>
                                                        <div className="text-[#93c5fd] text-sm">Monthly Listeners</div>
                                                    </div>
                                                </div>

                                                <blockquote className="bg-[#3b82f6]/10 border-l-4 border-[#FA6138] p-4 mb-8 italic text-[#c7d2fe]">
                                                    "{featuredStudy.quote}"
                                                    <footer className="text-[#818cf8]/70 text-sm mt-2">- {featuredStudy.artistName}</footer>
                                                </blockquote>

                                                <a
                                                    href={featuredStudy.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white px-8 py-4 rounded-xl hover:from-[#e55532] hover:to-[#FA6138] transition-all duration-300 font-medium group shadow-lg hover:shadow-xl"
                                                >
                                                    Read Full Case Study
                                                    <ExternalLink className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                                                </a>
                                            </div>

                                            <div className="relative">
                                                <div className="absolute inset-0 bg-gradient-to-br from-[#6366f1]/20 to-[#FA6138]/10 rounded-r-2xl" />
                                                <div className="relative p-8 lg:p-12 h-full flex flex-col justify-center">
                                                    <div className="text-center mb-8">
                                                        <img
                                                            src={require("../assets/images/karan.png")}
                                                            alt={featuredStudy.artistName}
                                                            className="w-32 h-32 rounded-full mx-auto mb-4 border-4 border-[#FA6138]/30 shadow-lg"
                                                        />
                                                        <h3 className="text-2xl font-bold text-[#c7d2fe] mb-2">{featuredStudy.artistName}</h3>
                                                        <p className="text-[#FA6138] font-medium uppercase tracking-wide text-sm">
                                                            {featuredStudy.genre} Artist
                                                        </p>
                                                    </div>

                                                    {/* Tools Used */}
                                                    <div className="mb-8">
                                                        <h4 className="text-[#a5b4fc] font-semibold mb-4">Tools Used:</h4>
                                                        <div className="space-y-2">
                                                            {featuredStudy.tools.map((tool, index) => (
                                                                <div key={index} className="flex items-center text-[#818cf8]/80 text-sm">
                                                                    <Star className="w-4 h-4 mr-2 text-[#FA6138] fill-current" />
                                                                    {tool}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Social Links */}
                                                    <div className="flex justify-center space-x-4">
                                                        {Object.entries(featuredStudy.socialLinks).map(([platform, url]) => (
                                                            <a
                                                                key={platform}
                                                                href={url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="w-10 h-10 bg-[#1e3a8a]/30 rounded-full flex items-center justify-center text-[#93c5fd] hover:text-[#FA6138] hover:bg-[#FA6138]/20 transition-all duration-300"
                                                            >
                                                                {platform === "spotify" && <Music className="w-5 h-5" />}
                                                                {platform === "instagram" && <Instagram className="w-5 h-5" />}
                                                                {platform === "youtube" && <Youtube className="w-5 h-5" />}
                                                                {platform === "twitter" && <Twitter className="w-5 h-5" />}
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Case Studies Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredCaseStudies
                                    .filter((study) => !study.featured || selectedGenre !== "all")
                                    .map((study, index) => (
                                        <motion.div
                                            key={study.id}
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                                            className="bg-gradient-to-br from-[#1e1b4b]/20 via-[#312e81]/15 to-[#4338ca]/10 backdrop-blur-xl border border-[#4f46e5]/20 rounded-2xl overflow-hidden group hover:border-[#a5b4fc]/30 hover:from-[#6366f1]/15 hover:to-[#1e1b4b]/20 transition-all duration-500"
                                            style={{
                                                boxShadow: "0 10px 30px rgba(79, 70, 229, 0.15), inset 0 1px 0 rgba(165, 180, 252, 0.1)",
                                            }}
                                        >
                                            <div className="p-6">
                                                <div className="flex items-center mb-4">
                                                    <img
                                                        src={require("../assets/images/karan.png")}
                                                        alt={study.artistName}
                                                        className="w-12 h-12 rounded-full mr-4 border-2 border-[#FA6138]/30"
                                                    />
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-[#c7d2fe]">{study.artistName}</h3>
                                                        <div className="flex items-center text-sm text-[#818cf8]/70">
                                                            <span className="uppercase tracking-wide">{study.genre}</span>
                                                            <span className="mx-2">•</span>
                                                            <MapPin className="w-3 h-3 mr-1" />
                                                            {study.location}
                                                        </div>
                                                    </div>
                                                </div>

                                                <h4 className="text-xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-2 leading-tight">
                                                    {study.title}
                                                </h4>
                                                <p className="text-[#FA6138] text-sm font-medium mb-3">{study.subtitle}</p>
                                                <p className="text-[#818cf8]/70 text-sm mb-6 leading-relaxed">{study.description}</p>

                                                {/* Key Results */}
                                                <div className="grid grid-cols-2 gap-3 mb-6">
                                                    <div className="bg-[#3b82f6]/10 rounded-lg p-3 text-center">
                                                        <div className="text-lg font-bold text-[#FA6138]">
                                                            {Object.values(study.results)[0]}
                                                        </div>
                                                        <div className="text-[#93c5fd] text-xs">Growth</div>
                                                    </div>
                                                    <div className="bg-[#3b82f6]/10 rounded-lg p-3 text-center">
                                                        <div className="text-lg font-bold text-[#FA6138]">
                                                            {Object.values(study.results)[1]}
                                                        </div>
                                                        <div className="text-[#93c5fd] text-xs">Listeners</div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center text-xs text-[#60a5fa]/60">
                                                        <Calendar className="w-3 h-3 mr-1" />
                                                        {study.timeline}
                                                    </div>
                                                    <a
                                                        href={study.link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center text-[#FA6138] hover:text-[#e55532] transition-colors text-sm font-medium group"
                                                    >
                                                        Read Story
                                                        <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                                                    </a>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                            </div>

                            {/* CTA Section */}
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 1.2 }}
                                className="text-center mt-20"
                            >
                                <div
                                    className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-12"
                                    style={{
                                        boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                                    }}
                                >
                                    <h2 className="text-3xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-6">
                                        Ready to Write Your Success Story?
                                    </h2>
                                    <p className="text-[#818cf8]/80 mb-8 max-w-2xl mx-auto text-lg">
                                        Join hundreds of independent artists who are already transforming their careers with UpNComer's
                                        AI-powered tools.
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                        <a
                                            href="/signup"
                                            className="inline-block bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white text-center
                           px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:from-[#e55532] hover:to-[#FA6138]
                           transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105"
                                        >
                                            START YOUR SUCCESS STORY
                                        </a>
                                        <a
                                            href="/demo"
                                            className="inline-flex items-center justify-center bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] 
                           px-8 py-4 text-sm font-medium tracking-wider rounded-xl hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40
                           transition-all duration-300 backdrop-blur-xl shadow-lg hover:shadow-xl"
                                        >
                                            <Play className="w-4 h-4 mr-2" />
                                            WATCH DEMO
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </main>
                </div>
            </div>
        </>
    )
}
